import { ISO8601 } from '../support/ISO8601';
import { State } from '@meraki-internal/state';
import { replaceItem } from '../utils/arrays';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { RECORDING_SERVICE_STATUS_EVENT, RECORDING_ERROR_EVENT, RecordingService } from './RecordingService';
import { getSuccessMessage, getFakeRecordingStats, IRecordingStats } from '@innerhive-internal/innerhive-common';
import * as RecordingMessages from './RecordingMessages';
import { RecordingStatsClient } from './RecordingStatsClient';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { Logger } from '../support/Logger';
import { CareMapState } from '../caremap/CareMapState';
import { IFreeTierLimits } from '../innerhive-plus/revenue-cat/IFreeTierLimits';
import { RevenueCatModel } from '../innerhive-plus/revenue-cat/RevenueCatModel';
import { APP_CALL_STATE_CHANGE, APP_FOREGROUNDED } from '../AppContainer';
import { CurrentRecordingStatus, VoiceRecorder } from 'capacitor-voice-recorder';
import { PhoneState } from 'capacitor-plugin-incoming-call';
import { AlertPresenter } from '../AlertBinder';
import { AppPrincipal } from '../auth/AppPrincipal';
import { isPlatform } from '../utils/isPlatform';
import { isAutomatedTests } from '../utils/isAutomatedTests';
import dayjs from 'dayjs';
import { NetworkAdapter } from '../support/NetworkAdapter';
import { OfflineRecordingQueue } from './OfflineRecordingQueue';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { ICaremapNoteResponse, NotesClient } from '../notes/model/NotesClient';
import { AsyncStorageProvider } from '../support/AsyncStorageProvider';
import { UserState } from '../user/UserState';
import { classifyRecording } from '@innerhive-internal/innerhive-common';
import { DevSettings } from '../support/DevSettings';

export type IRecordingStatus = 'recording-complete' | 'uploading' | 'uploaded' | 'upload-failed' | 'transcribed' | 'transcribe-failed' | 'summarized' | 'summarize-failed' | 'offline-pending';

export interface IRecordingSummary {
    title: string;
    participants?: string[];
    shortSummary: string; // listing
    highLevelSummary: string; // overview of what AI generates
    detailedSummary?: { // bulk of what AI generates
        keyTopicsDiscussed?: string[];
        decisionsMade?: string[];
        instructionsOrRecommendations?: string[];
    };
    actionItems?: {
        id: string;
        description: string;
        details?: string;
        completed?: boolean;
        completedAt?: ISO8601;
        completedBy?: string;
        category?: 'take-action-today' | 'schedule-these' | 'ongoing-care';
        when?: string;
        assignedTo?: string;
    }[];
    isUnavailable?: boolean;
    beaComment?: string;
}

export interface IExtractedFields {
    careRecipientName?: string;
    relationship?: string;
    diagnoses?: string[];
    allergies?: string[];
    medications?: string[];
    address?: string;
    additionalCareRecipientsMentioned?: string[];
}

/** Mirrors API response shape — present on IRecording.extractedMemoryItems when returned from the server */
export interface IExtractedMemoryItem {
    fact: string;
    category: string;
}

export interface IRecording {
    id: string;
    fileName: string;

    file: ArrayBuffer;
    url: string;

    // TODO remove noteId from this interface
    // It is specific to recordings that are in IndexedDB, and will be missing when part of a saved note
    noteId: string;

    status: IRecordingStatus;

    appointmentId?: string;

    // Care map this recording belongs to
    // Stored in IndexedDB but stripped when attached to note (note becomes source of truth)
    careMapUserId: string;
    careMapId: string;

    // The user who created this recording (principal.userId at time of recording)
    userId: string;

    summary?: IRecordingSummary;
    transcription?: string;
    skipAIProcessing?: boolean;
    summaryPrompt?: 'intro' | 'appointment';
    extractedFields?: IExtractedFields;
    extractedMemoryItems?: IExtractedMemoryItem[];

    mimeType: string;
    msDuration: number;
    byteLength: number;

    summaryRating?: 'positive' | 'negative';

    created: ISO8601;
    createdAtOverride?: ISO8601; // Optional override for dev testing
};

export type IRecordingData = Pick<IRecording, 'mimeType' | 'msDuration' | 'file'> & {
    createdAtOverride?: string;
};

type ICurrentRecordingExtras = {
    appointmentId?: string;
    initiatedFrom?: string;
    recordingContext?: 'at-appointment' | 'post-appointment' | 'exploring';
    summaryPrompt?: 'intro' | 'appointment';
};

type IRecordingState = {
    isRecording: boolean;
    isPaused: boolean;

    isSheetMinimized: boolean;

    recordings: IRecording[];
    offlineRecordings: IRecording[];
    recordingInterrupted?: boolean;
    recordingInterruptedOutgoing?: boolean;
    showRecordingInterruptedAlert? : boolean;

    // so things like buttons in Alerts can use the Recorder
    forceStartRecording?: boolean;
    currentRecordingExtras?: ICurrentRecordingExtras;

    // Network and offline state
    isOnline: boolean;
    pendingUploadCount: number;

    // Recording tips state
    tipsDismissedForSession: boolean;
    tipsDismissedPermanently: boolean;

    // Simulation state
    simulationCounter: number;

    // Recording stats
    recordingStats?: IRecordingStats;
};
export class RecordingState extends State<IRecordingState> {
    static inject = () => [
        RecordingService,
        BufferedEventBus,
        CaremapNotesModelFactory,
        CareMapState,
        RevenueCatModel,
        AlertPresenter,
        AppPrincipal,
        Logger,
        NetworkAdapter,
        OfflineRecordingQueue,
        MixPanelEventEmitter,
        AsyncStorageProvider,
        RecordingStatsClient,
        UserState,
        DevSettings,
        NotesClient
    ];

    private pendingRecordingCheck?: string;
    private shownProcessingCompleteIds = new Set<string>();
    private shownSuccessModalIds = new Set<string>();
    private shownErrorModalIds = new Set<string>();
    private currentCareMapId?: string;

    constructor(
        private service: RecordingService,
        private events: BufferedEventBus,
        private notesFactory: CaremapNotesModelFactory,
        private careMapState: CareMapState,
        private revenueCat: RevenueCatModel,
        private alert: AlertPresenter,
        private principal: AppPrincipal,
        private logger: Logger,
        private networkAdapter: NetworkAdapter,
        private offlineQueue: OfflineRecordingQueue,
        private mixpanel: MixPanelEventEmitter,
        private asyncStorage: AsyncStorageProvider,
        private recordingStatsClient: RecordingStatsClient,
        private userState: UserState,
        private devSettings: DevSettings,
        private notesClient: NotesClient
    ) {
        super({
            recordings: [],
            offlineRecordings: [],
            isRecording: false,
            isPaused: false,
            isSheetMinimized: false,
            isOnline: true,
            pendingUploadCount: 0,
            tipsDismissedForSession: false,
            tipsDismissedPermanently: false,
            simulationCounter: 0
        });

        this.load();

        this.events.on(RECORDING_SERVICE_STATUS_EVENT, (payload: IRecording | undefined) => {
            this.onRecordingStatusUpdated(payload);
            this.updateInitialWizardAnswer(payload);
        });

        // Listen for websocket note updates that might include recording status changes
        this.events.on('web-sockets.message', async (message: any) => {
            if (message.action === 'resourceChanged' && message.resourceToRefresh) {
                // Only check note resources, not sub-resources like reactions
                const isNoteResource = message.resourceToRefresh.match(/\/notes\/[^/]+$/);
                if (isNoteResource) {
                    // Store the resource to check after NotesModel finishes refreshing
                    this.pendingRecordingCheck = message.resourceToRefresh;
                }
            }
        });

        // Listen for NotesModel state changes to detect when notes have been updated
        this.notesFactory.subscribe(() => {
            if (this.pendingRecordingCheck) {
                this.checkForRecordingStatusChange(this.pendingRecordingCheck).catch(err =>
                    this.logger.error(`Error checking recording status change: ${err.toString()} ${JSON.stringify(err)}`)
                );
                this.pendingRecordingCheck = undefined;
            }
        });

        // Listen for UserState changes to load stats when user loads
        this.userState.subscribe(() => {
            if (this.userState.state.userId) {
                this.loadRecordingStats().catch(err =>
                    this.logger.error(`Error loading recording stats on user load: ${err.toString()} ${JSON.stringify(err)}`)
                );
            }
        });

        // Listen for network status changes
        this.networkAdapter.subscribe((state: {connected?: boolean}) => {
            const isOnline = state.connected ?? true;

            this.setState({
                isOnline
            });


            // When coming back online, try to process offline recordings and other recordings that may need uploading
            // Skip if dev setting is enabled (for testing UI with specific statuses)
            if (isOnline && (this.state.offlineRecordings.length > 0 || this.state.recordings.length > 0) && !this.devSettings.disableRecordingProcessing) {
                this.logger.info('Network restored, processing offline recordings', {
                    count: this.state.offlineRecordings.length
                });

                // Process offline recordings safely to avoid duplicates
                this.state.offlineRecordings.forEach(recording => {
                    if (!this.belongsToActiveCareMap(recording)) {
                        return;
                    }
                    this.processRecordingSafely(recording, async () => {
                        try {
                            await this.service.retryUpload(recording.id);
                        } catch (error) {
                            throw error;
                        }
                    });
                });

                // Also process regular recordings that might be in failed or incomplete states
                this.processRecordingsForUploadAndCleanup([...this.state.recordings, ...this.state.offlineRecordings], 'network-restore');

            }

            // Retry any pending direct uploads (recordings that bypassed storage)
            if (isOnline && this.service.hasPendingDirectUploads() && !this.devSettings.disableDirectUploadAutoRetry) {
                this.logger.info('Network restored, retrying pending direct uploads');
                this.service.retryAllPendingDirectUploads();
            }
        });

        // Listen for offline queue changes
        this.offlineQueue.subscribe(() => {
            this.setState({
                pendingUploadCount: this.offlineQueue.getPendingCount()
            });
        });

        // handle phone calls
        if (isPlatform('capacitor') && isPlatform('ios')) {
            this.events.on(APP_CALL_STATE_CHANGE, async (payload: PhoneState | undefined) => {
                try {
                    const { status } = await VoiceRecorder.getCurrentStatus() as CurrentRecordingStatus;

                    if (status === 'RECORDING') {
                        if (payload?.callState === 'RINGING') {
                            this.handleRecordingInterrupted();
                        } else if (payload?.callState === 'OUTGOING') {
                            this.setState({ recordingInterruptedOutgoing: true });
                        }
                    }
                } catch (error) {
                    throw error;
                }
            });

            // handle outgoing call on app foreground,
            // otherwise, if handling it above, recording was stuck in
            // Recording Complete but didn't exist / wasn't saved in indexedDB
            this.events.on(APP_FOREGROUNDED, async () => {
                if (this.state.recordingInterruptedOutgoing) {
                    this.handleRecordingInterrupted();
                }
            });
        }

        // Listen for care map changes to process orphaned recordings for the newly active care map
        this.careMapState.subscribe(() => {
            const careMap = this.careMapState.getActiveCaremap();

            // Only process when the care map ID actually changes
            if (careMap && careMap.careMapId !== this.currentCareMapId) {
                this.currentCareMapId = careMap.careMapId;

                // Notify subscribers so UI re-evaluates care-map-dependent filters
                // (e.g. getOfflineRecordingsForActiveCareMap)
                this.setState({});

                // When care map switches, try to process any orphaned recordings that belong to the new care map
                if (this.state.recordings.length > 0 || this.state.offlineRecordings.length > 0) {
                    this.logger.info('Care map changed, processing recordings for active care map');
                    this.processRecordingsForUploadAndCleanup(
                        [...this.state.recordings, ...this.state.offlineRecordings],
                        'network-restore'
                    );
                }
            }
        });

        // if recording is not interrupted, even on web we want to process uploads that may be orphaned or pending etc...
        this.events.on(APP_FOREGROUNDED, async () => {
            if (!this.state.recordingInterruptedOutgoing && !this.devSettings.disableRecordingProcessing) {
                // Load fresh recordings from IndexedDB and process for upload and cleanup
                // Skip if dev setting is enabled (for testing UI with specific statuses)
                try {
                    const allRecordings = (await this.service.getAll()).filter(r => this.belongsToActiveCareMap(r));
                    this.processRecordingsForUploadAndCleanup(allRecordings, 'load');
                } catch (err: any) {
                    // Tag dev flag simulation errors so Logger can filter them from Sentry
                    if (err?.message?.includes('dev flag enabled') || err?.message?.includes('Simulated:')) {
                        err.errorCode = 'dev-flag-simulation';
                    }
                    this.logger.error(err, { message: 'Error loading recordings on app foreground' });
                    // Storage errors are handled at runtime when user attempts to record
                }

                // Check if any "processing" recordings have completed while app was backgrounded
                this.checkProcessingRecordingsOnForeground().catch(err =>
                    this.logger.error(err, { message: 'Error checking processing recordings on foreground' })
                );
            }
        });
    };

    private hasProcessedLocalRecordings = false;
    private processingRecordings = new Set<string>();
    private processingNotes = new Set<string>();

    private NUM_RECORDINGS_QUESTION = 'num-recordings';
    private NUM_AI_PROCESSED_RECORDINGS_QUESTION = 'num-ai-processed-recordings';
    private TIPS_DISMISSED_PERMANENTLY_KEY = 'recording-tips-dismissed-permanently';

    private tipsDismissalStorage: ReturnType<AsyncStorageProvider['getBooleanProvider']> | undefined;

    private getTipsDismissalStorage() {
        if (!this.tipsDismissalStorage) {
            try {
                this.tipsDismissalStorage = this.asyncStorage.getBooleanProvider(
                    this.TIPS_DISMISSED_PERMANENTLY_KEY,
                    { envNeutral: false, userNeutral: false }
                );
            } catch (err: any) {
                this.logger.error(`Failed to create tips dismissal storage provider: ${err.toString()} ${JSON.stringify(err)}`);
                throw err;
            }
        }
        return this.tipsDismissalStorage;
    }

    private async processRecordingSafely(recording: IRecording, action: () => Promise<void> | void) {
        if (this.processingRecordings.has(recording.id)) {
            return;
        }

        this.processingRecordings.add(recording.id);
        try {
            await action();
        } catch (err: any) {
            // Tag dev flag simulation errors so Logger can filter them from Sentry
            if (err?.message?.includes('dev flag enabled') || err?.message?.includes('Simulated:')) {
                err.errorCode = 'dev-flag-simulation';
            }
            this.logger.error(err, { message: 'Error processing recording', recording });
            throw err;
        } finally {
            this.processingRecordings.delete(recording.id);
        }
    }

    private async processNoteSafely(noteId: string, action: () => Promise<void> | void) {
        if (this.processingNotes.has(noteId)) {
            this.logger.info('Note already being processed, skipping', { noteId });
            return;
        }

        this.processingNotes.add(noteId);
        try {
            await action();
        } finally {
            this.processingNotes.delete(noteId);
        }
    }

    /**
     * Check if a recording belongs to the currently active care map and current user.
     * Returns true for legacy recordings without careMapId for backward compatibility.
     * If userId or careMapUserId are present, they must also match.
     */
    private belongsToActiveCareMap(recording: IRecording): boolean {
        const activeCaremap = this.careMapState.getActiveCaremap();
        const activeCareMapId = activeCaremap?.careMapId;

        // Handle legacy recordings without careMapId
        if (!recording.careMapId) {
            return true;
        }

        if (recording.careMapId !== activeCareMapId) {
            return false;
        }

        // Check care map owner (full composite key)
        if (recording.careMapUserId) {
            if (recording.careMapUserId !== (activeCaremap && 'userId' in activeCaremap ? activeCaremap.userId : undefined)) {
                return false;
            }
        }

        // Check recording creator matches logged-in user
        if (recording.userId) {
            if (recording.userId !== this.principal.userId) {
                return false;
            }
        }

        return true;
    }

    private shouldProcessRecording(recording: IRecording, hasValidNote: boolean): { process: boolean; createNote: boolean } {
        // Skip recordings that are fully uploaded with valid note and URL
        // These don't need reprocessing when switching care maps
        if (recording.status === 'uploaded' && recording.url && recording.noteId && hasValidNote) {
            return { process: false, createNote: false };
        }

        // Statuses that need processing
        const needsUpload = ['uploading', 'upload-failed', 'recording-complete', 'offline-pending'].includes(recording.status);

        if (hasValidNote && needsUpload) {
            // Has a note but needs upload retry
            return { process: true, createNote: false };
        }

        // Handle recordings without valid local note
        if (!hasValidNote && (recording.status === 'offline-pending' || recording.status === 'upload-failed')) {
            if (recording.status === 'offline-pending') {
                this.mixpanel.track('Processing Offline Recording Without Note', () => ({ 'Recording': recording }));
            }

            // Key distinction: does noteId exist?
            if (!recording.noteId) {
                // No noteId - need to create note first
                return { process: true, createNote: true };
            } else {
                // Has noteId but note not in local state - use existing noteId
                return { process: true, createNote: false };
            }
        }

        if (!hasValidNote && recording.status === 'uploading' && !recording.noteId) {
            this.mixpanel.track('Processing Offline Recording Without Note', () => ({ 'Recording': recording }));
            return { process: true, createNote: true };
        }

        return { process: false, createNote: false };
    }

    private processRecordingsForUploadAndCleanup = (recordings: IRecording[], context: 'load' | 'network-restore' = 'load') => {
        // Skip processing if dev setting is enabled (for testing UI with specific statuses)
        if (this.devSettings.disableRecordingProcessing) {
            return;
        }

        const localRecordingsToTrack: IRecording[] = [];

        recordings.forEach(recording => {
            // Only process recordings that belong to the currently active care map
            // This prevents recordings from other care maps from interfering
            if (!this.belongsToActiveCareMap(recording)) {
                return;
            }

            const note = this.findNote(recording.noteId);
            const hasValidNote = !!(note && recording.noteId);

            // 1. Cleanup: Delete recordings that are already uploaded to notes
            if (this.isUploadedToNote(recording) && this.isUploadedStatus(recording.status)) {
                this.service.delete(recording.id);
            }

            // 2. Handle recordings that need processing
            const needsProcessing = this.shouldProcessRecording(recording, hasValidNote);
            if (needsProcessing.process) {
                this.processRecordingSafely(recording, () => {
                    if (needsProcessing.createNote) {
                        this.service.retryUpload(recording.id);
                    } else {
                        this.service.upload(recording);
                    }
                });
            }

            // 3. Handle out-of-sync status updates
            if (recording.status === 'uploaded' && note?.otherAttributes?.recording?.status === 'uploading') {
                this.onRecordingStatusUpdated(recording);
            }

            // Only do logging and tracking during load, not network restore
            if (context === 'load') {
                // if there is an orphaned recording, it is completed but not moved on or upload failed,
                // it might be stuck in indexedDB so let's log it to Sentry
                // doing here because need to make sure notesModel.hasLoaded before checking
                // Only log orphaned recordings that belong to the active care map
                if (!isAutomatedTests() &&
                    dayjs(recording.created).isAfter('2025-07-07') &&
                    this.belongsToActiveCareMap(recording) &&
                    (!this.isUploadedToNote(recording) ||
                    // this can be duplicative with the one above that kicks off the upload
                    recording.status === 'recording-complete' ||
                    recording.status === 'upload-failed' ||
                    recording.status === 'offline-pending')) {
                    this.logger.error('Potentially Orphaned Recording', recording);
                }

                // build failing in github, try again
                // if there are any recordings found locally, collect them for batch tracking
                if (!isAutomatedTests() &&
                    dayjs(recording.created).isAfter('2025-07-07')) {
                    localRecordingsToTrack.push(recording);
                }
            }
        });

        // Send batch events for all local recordings found
        if (context === 'load' && localRecordingsToTrack.length > 0) {
            // Separate recordings by whether they match the active care map
            const matchedRecordings = localRecordingsToTrack.filter(r => this.belongsToActiveCareMap(r));
            const mismatchedRecordings = localRecordingsToTrack.filter(r => !this.belongsToActiveCareMap(r));

            if (matchedRecordings.length > 0) {
                this.mixpanel.track('Local Recordings Found', () => ({
                    'Recordings': matchedRecordings
                }));
            }

            if (mismatchedRecordings.length > 0) {
                this.mixpanel.track('Local Recordings Found - Mismatched Care Profile', () => ({
                    'Recordings': mismatchedRecordings
                }));
            }
        }
    };

    load = async () => {
        let allRecordings: IRecording[] = [];
        try {
            allRecordings = await this.service.getAll();
        } catch (err: any) {
            // Tag dev flag simulation errors so Logger can filter them from Sentry
            if (err?.message?.includes('dev flag enabled') || err?.message?.includes('Simulated:')) {
                err.errorCode = 'dev-flag-simulation';
            }
            this.logger.error(err, { message: 'Error loading recordings on initial load' });
            // Storage errors are handled at runtime when user attempts to record
            return; // Exit early since we can't load recordings
        }

        const onlineRecordings = allRecordings.filter(r => r.status !== 'offline-pending');
        const offlineRecordings = allRecordings.filter(r => r.status === 'offline-pending');

        let isOnline: boolean;
        try {
            isOnline = this.networkAdapter.isOnline;
        } catch (error) {
            // NetworkAdapter not yet initialized, default to true
            isOnline = true;
        }

        this.setState({
            recordings: onlineRecordings,
            offlineRecordings,
            isOnline,
            pendingUploadCount: this.offlineQueue.getPendingCount()
        });

        try {
            this.notesFactory.forActiveCaremap().subscribe(notesModel => {
                // only check on the first load and make sure notes loaded first
                if (!this.hasProcessedLocalRecordings && notesModel.hasLoaded) {
                    // Process both online and offline recordings
                    this.processRecordingsForUploadAndCleanup([...this.state.recordings, ...this.state.offlineRecordings], 'load');

                    // doing here because need to make sure notesModel.hasLoaded before checking
                    this.hasProcessedLocalRecordings = true;
                }
            });
        } catch (error) {
            // TODO: forActiveCaremap throws an error for anonymous
        }
    };

    private handleRecordingInterrupted = async () => {
        this.setState({ recordingInterrupted: true });

        // wait a sec before showing prompt
        await new Promise(res => setTimeout(res, 2000));
        this.setState({ showRecordingInterruptedAlert: true });
    };

    start = () => {
        // Don't set a default recording context - allow it to be undefined for menu bar recordings
        // Context buttons explicitly set their context before recording starts

        // Start recording and reset session tips dismissal for new recording session
        this.setState({
            isRecording: true,
            tipsDismissedForSession: false
        });

        // Force reload of permanent dismissal preference
        this.tipsDismissalLoaded = false;
        this.loadTipsDismissalPreference();
    };

    stop = () => {
        // Note: currentRecordingExtras is intentionally NOT cleared here.
        // add() reads extras (summaryPrompt, appointmentId) after stop() is called,
        // then clears them itself. Clearing here would wipe data before it's consumed.
        this.setState({
            isPaused: false,
            isRecording: false,
            forceStartRecording: false
        });
    };

    pause = () => {
        this.setState({ isPaused: true });
    };

    resume = () => {
        this.setState({ isPaused: false });
    };

    minimizeSheet = () => {
        this.setState({ isSheetMinimized: true });
    };

    maximizeSheet = () => {
        this.setState({ isSheetMinimized: false });
    };

    toggleSheet = () => {
        this.setState({ isSheetMinimized: !this.state.isSheetMinimized });
    };

    isPaused = () => this.state.isPaused;

    isRecording = () => this.state.isRecording;

    isSheetMinimized = () => this.state.isSheetMinimized;

    add = async (recording: IRecordingData, autoSaved?: boolean) => {
        const wasInterrupted = this.state.recordingInterrupted || false;

        const newRecording = await this.service.add(
            recording,
            {
                autoSaved,
                skipAIProcessing: this.shouldSkipAIProcessing(),
                appointmentId: this.state.currentRecordingExtras?.appointmentId,
                summaryPrompt: this.state.currentRecordingExtras?.summaryPrompt,
                wasInterrupted
            }
        );

        // Add to appropriate list based on status
        if (newRecording.status === 'offline-pending') {
            this.setState({
                offlineRecordings: [...this.state.offlineRecordings, newRecording],
                currentRecordingExtras: undefined
            });
        } else {
            this.setState({
                recordings: [...this.state.recordings, newRecording],
                currentRecordingExtras: undefined
            });
        }
    };

    /**
     * Returns true if the next recording should be treated as an intro recording.
     * Matches the same condition used by the home page to show RecordingEmptyState:
     * no notes with recordings and no offline recordings.
     */
    shouldUseIntroFlow = (): boolean => {
        try {
            const notes = this.notesFactory.forActiveCaremap().getNotes();
            const hasNotesWithRecordings = notes.some(n => n.otherAttributes?.recording);
            const hasOfflineRecordings = this.state.offlineRecordings.length > 0;
            return !hasNotesWithRecordings && !hasOfflineRecordings;
        } catch {
            // No active caremap — still show intro
            return true;
        }
    };

    // assumes NotesModel.hasLoaded = true
    findNote = (noteId: string) => {
        if (!this.careMapState.hasActiveCareMap()) {
            return undefined;
        }
        const notes = this.notesFactory.forActiveCaremap().getNotes();
        return notes.find(n => n.noteId === noteId);
    };

    // assumes NotesModel.hasLoaded = true
    isUploadedToNote = (recording: IRecording) => {
        const recordingNote = this.findNote(recording.noteId);
        return recordingNote?.attachments.find(a => a.url === recording.url) ? true : false;
    };

    isUploadedStatus = (status: IRecordingStatus | undefined) => {
        return status && status !== 'recording-complete' && status !== 'uploading' && status !== 'upload-failed' && status !== 'offline-pending';
    };

    onRecordingStatusUpdated = async (payload: IRecording | undefined) => {
        if (payload) {
            // Only process recordings that belong to the currently active care map
            // because we need to be able to find and update the note
            if (!this.belongsToActiveCareMap(payload)) {
                return;
            }

            // Use note-based safe processing to prevent race conditions with other note updates
            await this.processNoteSafely(payload.noteId, async () => {
                try {
                    const noteVM = await this.notesFactory.createEditForm(payload.noteId);
                    let note = noteVM.getNote();

                    // update otherAttributes.recording
                    note = {
                        ...note,
                        otherAttributes: {
                            ...note.otherAttributes,
                            recording: this.service.stripRecordingDataForNote(payload)
                        }
                    };

                    if (payload.status === 'uploaded') {
                        // add the attachment
                        note = {
                            ...note,
                            attachments: [
                                ...note.attachments,
                                {
                                    contentType: payload.mimeType,
                                    fileName: payload.fileName,
                                    url: payload.url
                                }
                            ]
                        };
                    }

                    noteVM.setNote(note);

                    // skip certain Mixpanel tracking
                    await noteVM.save({ skipTracking: true });

                    // Emit processing complete event when recording is summarized or processing is complete without summary
                    if (payload.status === 'summarized' || payload.status === 'recording-complete') {
                        await this.emitProcessingComplete(payload);
                    }

                    if (payload.status === 'uploaded') {
                        // Update num-recordings wizard answer now that recording is uploaded and attached to note
                        let numRecordings = this.getNumCareMapRecordings();
                        this.careMapState.answerInitialWizardQuestion({
                            questionId: this.NUM_RECORDINGS_QUESTION,
                            answer: ++numRecordings
                        });

                        // For uploaded recordings, we've just added the attachment and saved the note
                        // so we can safely delete from IndexedDB. The timing issue with isUploadedToNote
                        // can cause uploaded recordings to not be cleaned up properly.
                        this.service.delete(payload.id);

                        // Update the recording status in state first, then remove after a brief delay
                        // This ensures the UI shows the correct 'uploaded' status before cleanup
                        const wasOffline = this.state.offlineRecordings.find((r: IRecording) => r.id === payload.id);
                        if (wasOffline) {
                            this.setState({
                                offlineRecordings: replaceItem(this.state.offlineRecordings, payload, 'id')
                            });
                        } else {
                            this.setState({
                                recordings: replaceItem(this.state.recordings, payload, 'id')
                            });
                        }

                        // Remove from state after a short delay to allow UI to update
                        setTimeout(() => {
                            this.setState({
                                recordings: this.state.recordings.filter((r: IRecording) => r.id !== payload.id),
                                offlineRecordings: this.state.offlineRecordings.filter((r: IRecording) => r.id !== payload.id)
                            });
                        }, 500); // 500ms delay - enough for UI to show uploaded status
                    } else {
                        // Update in appropriate list based on status
                        if (payload.status === 'offline-pending') {
                            this.setState({
                                offlineRecordings: replaceItem(this.state.offlineRecordings, payload, 'id')
                            });
                        } else {
                            // Move from offline to online list if status changed
                            const wasOffline = this.state.offlineRecordings.find((r: IRecording) => r.id === payload.id);
                            if (wasOffline) {
                                this.setState({
                                    recordings: [...this.state.recordings, payload],
                                    offlineRecordings: this.state.offlineRecordings.filter((r: IRecording) => r.id !== payload.id)
                                });
                            } else {
                                this.setState({
                                    recordings: replaceItem(this.state.recordings, payload, 'id')
                                });
                            }
                        }
                    }
                } catch (error: any) {
                    console.log(error);

                    // Don't log to Sentry during automated tests
                    if (!isAutomatedTests()) {
                        // Tag dev flag simulation errors so Logger can filter them from Sentry
                        if (error?.message?.includes('dev flag enabled') || error?.message?.includes('Simulated:')) {
                            error.errorCode = 'dev-flag-simulation';
                        }
                        this.logger.error(error);
                    }
                }
            });
        }
    };

    updateInitialWizardAnswer = (_payload: IRecording | undefined) => {
        // Wizard answers are now updated in emitProcessingComplete() when status becomes 'summarized'
        // This prevents issues with care map switching during recording creation
    };

    getOfflineRecordingsForActiveCareMap(): IRecording[] {
        return (this.state.offlineRecordings || []).filter(r => this.belongsToActiveCareMap(r));
    }

    private getCareMap = () => {
        return this.careMapState.getActiveCaremap();
    };

    private getNumCareMapRecordings = (): number => {
        return this.getCareMap()?.initialWizardAnswers[this.NUM_RECORDINGS_QUESTION] || 0;
    };

    private isRecordingLimitReached = (limits: IFreeTierLimits) => {
        return this.getNumCareMapRecordings() >= limits.freeRecordings;
    };

    private getNumCareMapAIProcessedRecordings = (): number => {
        return this.getCareMap()?.initialWizardAnswers[this.NUM_AI_PROCESSED_RECORDINGS_QUESTION] || 0;
    };

    private shouldSkipAIProcessing = () => {
        // Force skip AI during automated tests (except when explicitly enabled)
        if (isAutomatedTests() && localStorage.getItem('ENABLE_AI_PROCESSING') !== 'true') {
            return true;
        }
        return !this.revenueCat.hasInnerhivePlus() && this.isRecordingAILimitReached();
    };

    isRecordingAILimitReached = () => {
        const limits = this.revenueCat.getFreeTierLimits();
        return this.getNumCareMapAIProcessedRecordings() >= limits.freeRecordingAIProcessing;
    };

    requiresInnerhivePlus = (limits: IFreeTierLimits) => {
        return this.isRecordingLimitReached(limits);
    };

    getInnerhivePlusTriggerSource = () => {
        const limits = this.revenueCat.getFreeTierLimits();
        return this.isRecordingLimitReached(limits) ? 'recordings' : 'recording ai processing';
    };

    findNoteForAppointment = (apptId: string): ICaremapNoteResponse | undefined => {
        const notes = this.notesFactory.forActiveCaremap().getNotes();
        return notes.find(note => note?.otherAttributes?.recording?.appointmentId === apptId);
    };

    updateCurrentRecordingExtras = (extras: Partial<ICurrentRecordingExtras>) => {
        this.setState({
            currentRecordingExtras: {...this.state.currentRecordingExtras, ...extras}
        });
    };

    // Triggers recording via a useEffect in Recorder that watches forceStartRecording.
    // This indirection is needed because recording startup logic (permissions, consent,
    // VoiceRecorder) lives in the Recorder component, not in this state class.
    triggerRecording = (extras?: Partial<ICurrentRecordingExtras>) => {
        if (extras) {
            this.updateCurrentRecordingExtras(extras);
        }
        this.setState({ forceStartRecording: true });
    };

    toggleActionItemCompletion = async (noteId: string, actionItemId: string) => {
        try {
            const noteVM = await this.notesFactory.createEditForm(noteId);
            let note = noteVM.getNote();

            if (note.otherAttributes?.recording?.summary?.actionItems) {
                const actionItems = note.otherAttributes.recording.summary.actionItems.map(item => {
                    if (item.id === actionItemId) {
                        const isCompleted = !item.completed;
                        return {
                            ...item,
                            completed: isCompleted,
                            completedAt: isCompleted ? new Date().toISOString() as ISO8601 : undefined,
                            completedBy: isCompleted ? this.principal.userId : undefined
                        };
                    }
                    return item;
                });

                note = {
                    ...note,
                    otherAttributes: {
                        ...note.otherAttributes,
                        recording: {
                            ...note.otherAttributes.recording,
                            summary: {
                                ...note.otherAttributes.recording.summary,
                                actionItems
                            }
                        }
                    }
                };

                noteVM.setNote(note);
                await noteVM.save({ skipTracking: true });
            }
        } catch (error: any) {
            error.displayMessage = 'Unable to toggle action item, please try again.';
            this.alert.showAndLogError(error);
        }
    };

    saveSummaryRating = async (noteId: string, summaryRating: 'positive' | 'negative') => {
        try {
            const note = this.findNote(noteId);
            if (note?.links?.self) {
                await this.notesClient.putSummaryRating(note, summaryRating);
            }
        } catch (error: any) {
            error.displayMessage = 'Unable to save rating, please try again.';
            this.alert.showAndLogError(error);
        }
    };

    getActionItemCompletionStats = (recording: IRecording) => {
        const actionItems = recording.summary?.actionItems || [];
        const completed = actionItems.filter(item => item.completed).length;
        const total = actionItems.length;
        return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
    };

    getActionItemsByCategory = (recording: IRecording) => {
        const actionItems = recording.summary?.actionItems || [];

        const categorized = {
            'take-action-today': actionItems.filter(item => item.category === 'take-action-today'),
            'schedule-these': actionItems.filter(item => item.category === 'schedule-these'),
            'ongoing-care': actionItems.filter(item => item.category === 'ongoing-care'),
            'uncategorized': actionItems.filter(item => !item.category)
        };

        return categorized;
    };

    getCategoryDisplayName = (category: string) => {
        const categoryMap: Record<string, string> = {
            'take-action-today': 'Take Action Today',
            'schedule-these': 'Schedule These',
            'ongoing-care': 'Ongoing Care',
            'uncategorized': 'Other Actions'
        };
        return categoryMap[category] || category;
    };

    getCategoryCompletionStats = (recording: IRecording, category: string) => {
        const categorizedItems = this.getActionItemsByCategory(recording);
        const items = (categorizedItems as any)[category] || [];
        const completed = items.filter((item: any) => item.completed).length;
        const total = items.length;
        return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
    };

    // Offline recording management
    getOfflineRecordings = (): IRecording[] => {
        return this.state.offlineRecordings;
    };

    getAllRecordings = (): IRecording[] => {
        return [...this.state.recordings, ...this.state.offlineRecordings];
    };

    isOnline = (): boolean => {
        return this.state.isOnline;
    };

    getPendingUploadCount = (): number => {
        return this.state.pendingUploadCount;
    };

    retryOfflineRecording = async (recordingId: string) => {

        await this.service.retryUpload(recordingId);
    };

    hasOfflineRecordings = (): boolean => {
        return this.state.offlineRecordings.length > 0;
    };

    getRecordingStatus = (recordingId: string): IRecordingStatus | undefined => {
        const recording = this.getAllRecordings().find(r => r.id === recordingId);
        return recording?.status;
    };

    // Force sync all offline recordings
    syncOfflineRecordings = async () => {
        if (!this.networkAdapter.isOnline) {
            return;
        }

        for (const recording of this.state.offlineRecordings) {
            await this.service.retryUpload(recording.id);
        }
    };


    // Get current recording context
    getCurrentRecordingContext = (): 'at-appointment' | 'post-appointment' | 'exploring' | undefined => {
        return this.state.currentRecordingExtras?.recordingContext;
    };

    // Recording tips management
    private tipsDismissalLoaded = false;

    private loadTipsDismissalPreference = async () => {
        if (this.tipsDismissalLoaded) {
            return;
        }

        try {
            const dismissed = await this.getTipsDismissalStorage().get();
            this.setState({ tipsDismissedPermanently: dismissed === true });
            this.tipsDismissalLoaded = true;
        } catch (err: any) {
            this.logger.error(`Failed to load tips dismissal preference: ${err.toString()} ${JSON.stringify(err)}`);
            // Don't mark as loaded if the storage provider isn't ready yet
            // This allows retry when the provider becomes available
            const errorMessage = err instanceof Error ? err.message : String(err);
            const isStorageNotReady = errorMessage.includes('userId is not yet known') ||
                                     errorMessage.includes('env is not yet known');
            if (!isStorageNotReady) {
                this.tipsDismissalLoaded = true; // Mark as loaded for other errors to prevent infinite retries
            }
        }
    };

    shouldShowTips = (): boolean => {
        // Trigger async loading if not already loaded
        if (!this.tipsDismissalLoaded) {
            this.loadTipsDismissalPreference();
        }

        return !this.state.tipsDismissedForSession && !this.state.tipsDismissedPermanently;
    };

    dismissTipsForSession = () => {
        this.setState({ tipsDismissedForSession: true });
    };

    dismissTipsPermanently = async () => {
        try {
            await this.getTipsDismissalStorage().set(true);
            this.setState({ tipsDismissedPermanently: true });
        } catch (err: any) {
            this.logger.error(`Failed to save tips dismissal preference: ${err.toString()} ${JSON.stringify(err)}`);
        }
    };

    removePermanentTipsDismissal = async () => {
        try {
            await this.getTipsDismissalStorage().remove();
            this.setState({ tipsDismissedPermanently: false });
        } catch (err: any) {
            this.logger.error(`Failed to remove tips dismissal preference: ${err.toString()} ${JSON.stringify(err)}`);
        }
    };

    // Save dismissal preference to storage without affecting current session
    saveTipsDismissalPreference = async (dismissed: boolean) => {
        try {
            if (dismissed) {
                await this.getTipsDismissalStorage().set(true);
            } else {
                await this.getTipsDismissalStorage().remove();
            }
        } catch (err: any) {
            this.logger.error(`Failed to save tips dismissal preference: ${err.toString()} ${JSON.stringify(err)}`);
        }
    };

    hasShownProcessingComplete = (recordingId: string): boolean => {
        return this.shownProcessingCompleteIds.has(recordingId);
    };

    markProcessingCompleteShown = (recordingId: string): void => {
        this.shownProcessingCompleteIds.add(recordingId);
    };

    hasShownSuccessModal = (recordingId: string): boolean => {
        return this.shownSuccessModalIds.has(recordingId);
    };

    markSuccessModalShown = (recordingId: string): void => {
        this.shownSuccessModalIds.add(recordingId);
    };

    hasShownErrorModal = (recordingId: string): boolean => {
        return this.shownErrorModalIds.has(recordingId);
    };

    markErrorModalShown = (recordingId: string): void => {
        this.shownErrorModalIds.add(recordingId);
    };

    checkStorageConnection = async (): Promise<boolean> => {
        try {
            await this.service.checkStorageConnection();
            return true;
        } catch (error) {
            // Storage unavailable - check if we can proceed with direct upload
            if (this.networkAdapter.isOnline) {
                // Online: allow recording, direct upload will handle it
                return true;
            }

            // Offline + storage failed = unrecoverable, block recording
            // Add small delay to allow consent modal to close smoothly
            await new Promise(resolve => setTimeout(resolve, 350));
            this.showStorageUnavailableError();
            return false;
        }
    };

    private showStorageUnavailableError = () => {
        const isWeb = !isPlatform('capacitor');
        const message = isWeb
            ? 'Recording requires either an internet connection or working browser storage. Try closing this tab and reopening the app, or connect to the internet.'
            : 'Recording requires either an internet connection or working device storage. Try closing and restarting the app, or connect to the internet.';

        this.events.emit(RECORDING_ERROR_EVENT, {
            recordingId: 'storage-unavailable',
            errorType: 'storage-and-network-failed',
            title: 'Quick Fix Needed',
            message
        });
    };

    private getShortRecordingMessage(): string {
        return RecordingMessages.getShortRecordingMessage();
    }

    private emitProcessingComplete = async (recording: IRecording) => {
        try {
            // Classify the recording for short recording detection
            const classification = classifyRecording(recording);

            // Update num-ai-processed-recordings wizard answer now that AI processing is complete
            // Only update if recording belongs to the currently active care map
            // Note: num-recordings is updated earlier when recording is uploaded (see onRecordingStatusUpdated)
            // Skip updating for short recordings without summaries
            if (this.belongsToActiveCareMap(recording) && !(classification.isShort && !classification.hasSummary)) {
                let numAIProcessedRecordings = this.getNumCareMapAIProcessedRecordings();
                this.careMapState.answerInitialWizardQuestion({
                    questionId: this.NUM_AI_PROCESSED_RECORDINGS_QUESTION,
                    answer: ++numAIProcessedRecordings
                });
            }

            // Fetch real stats from API
            const stats = await this.recordingStatsClient.getRecordingStats();
            const message = getSuccessMessage(stats);

            // Update cached stats
            this.setState({ recordingStats: stats });

            // Always emit PROCESSING_COMPLETE with classification data
            // The event handler will decide which modal to show
            // Only include shortRecordingMessage if dev flag allows it
            this.events.emit('PROCESSING_COMPLETE', {
                message,
                stats,
                recordingId: recording.id,
                noteId: recording.noteId,
                classification,
                msDuration: recording.msDuration,
                summaryPrompt: recording.summaryPrompt,
                shortRecordingMessage: (classification.isShort && !classification.hasSummary && !this.devSettings.ignoreShortRecordingMessages)
                    ? this.getShortRecordingMessage()
                    : undefined
            });
        } catch (err: any) {
            this.logger.error(`Failed to fetch recording stats: ${err.toString()} ${JSON.stringify(err)}`);
            // Don't emit the event if we can't get stats
        }
    };

    // Message Selection Algorithm
    getSuccessMessage(isOffline?: boolean): { title: string; message: string } {
        return RecordingMessages.getSuccessMessage(isOffline);
    }

    // Check if a note update includes a recording status change to 'summarized'
    private checkForRecordingStatusChange = async (noteResource: string) => {
        try {
            // Get the updated note from NotesModel (which has already been refreshed)
            const notesModel = this.notesFactory.forActiveCaremap();
            const note = notesModel.getNotes().find(n => n.links.self.href === noteResource);

            if (!note) {
                return;
            }

            if (note.otherAttributes?.recording) {
                const recording = note.otherAttributes.recording as IRecording;

                // Check if this recording just became 'summarized' or 'recording-complete' (e.g. no speech detected)
                if (recording.status === 'summarized' || recording.status === 'recording-complete') {
                    // Check if we already know about this recording having this status
                    const existingRecording = this.getAllRecordings().find(r => r.id === recording.id);

                    // Only emit if this is a new status (not already at this status)
                    if (!existingRecording || existingRecording.status !== recording.status) {
                        await this.emitProcessingComplete(recording);
                    }
                }
            }
        } catch (error) {
            // Ignore errors - note might not exist or not be accessible
            // This is expected for non-note resources or notes without recordings
        }
    };

    // On app foreground, check if any recordings that were being processed on the backend
    // have completed while the app was backgrounded (and we missed the websocket message).
    private checkProcessingRecordingsOnForeground = async () => {
        if (!this.careMapState.hasActiveCareMap()) {
            return;
        }

        const notesModel = this.notesFactory.forActiveCaremap();
        if (!notesModel.hasLoaded) {
            return;
        }

        // Find notes with recordings in a "backend is processing" state
        const processingNotes = notesModel.getNotes().filter(note => {
            const status = (note.otherAttributes?.recording as IRecording | undefined)?.status;
            return status === 'uploaded' || status === 'transcribed';
        });

        for (const note of processingNotes) {
            const noteResource = note.links.self.href;
            try {
                await notesModel.refresh(noteResource);
                await this.checkForRecordingStatusChange(noteResource);
            } catch (err: any) {
                this.logger.error(err, { message: 'Error checking processing recording on foreground' });
            }
        }
    };

    loadRecordingStats = async () => {
        try {
            const stats = await this.recordingStatsClient.getRecordingStats();
            this.setState({ recordingStats: stats });
        } catch (err: any) {
            // Don't log this error when automated tests are running
            if (!isAutomatedTests()) {
                this.logger.error(`Failed to load recording stats: ${err.toString()} ${JSON.stringify(err)}`);
            }
        }
    };

    simulateRecordingSuccess = () => {
        const currentCounter = this.state.simulationCounter;
        const newCounter = currentCounter + 1;
        this.setState({ simulationCounter: newCounter });

        // getFakeRecordingStats already adds 1, so pass current counter
        const stats = getFakeRecordingStats(currentCounter);
        const message = getSuccessMessage(stats);

        this.events.emit('PROCESSING_COMPLETE', {
            message,
            stats,
            recordingId: `simulated-${newCounter}`,
            noteId: `simulated-note-${newCounter}`
        });
    };

};
